/**
 * Union-Find data structure for grouping elements.
 */
class UnionFind {
  private parent: Map<number, number> = new Map();

  constructor(size: number) {
    for (let i = 0; i < size; i++) {
      this.parent.set(i, i);
    }
  }

  /**
   * Find the root of the set containing element i, with path compression.
   */
  find(i: number): number {
    // The constructor guarantees every key 0..size-1 has an entry, and
    // `union` only changes existing values — so `get` never returns undefined
    // for a key that's been previously set. Asserted with `!`.
    const parent = this.parent.get(i)!;
    if (parent !== i) {
      this.parent.set(i, this.find(parent));
    }
    return this.parent.get(i)!;
  }

  /**
   * Union two sets containing elements i and j.
   */
  union(i: number, j: number): void {
    const rootI = this.find(i);
    const rootJ = this.find(j);
    if (rootI !== rootJ) {
      this.parent.set(rootI, rootJ);
    }
  }

  /**
   * Group elements by their root and return as array of index arrays.
   */
  getGroups(): number[][] {
    const groups = new Map<number, number[]>();
    for (let i = 0; i < this.parent.size; i++) {
      const root = this.find(i);
      if (!groups.has(root)) {
        groups.set(root, []);
      }
      groups.get(root)!.push(i);
    }
    return Array.from(groups.values());
  }
}

export { UnionFind };
